import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";
import ListingStatsCard from "../components/ListingStatsCard";
import PublicLinkError from "../components/PublicLinkError";
import RealtorCredibilityCard from "../components/RealtorCredibilityCard";
import PublicEngagementStrip from "../components/PublicEngagementStrip";
import LiveViewerBadge from "../components/LiveViewerBadge";
import ShareRow from "../components/ShareRow";
import PublicInquiriesPanel from "../components/PublicInquiriesPanel";
import EmbedSnippet from "../components/EmbedSnippet";
import OtherListingsStrip from "../components/OtherListingsStrip";
import BookingsPanel from "../components/BookingsPanel";
import RealtorChatbotBubble from "../components/RealtorChatbotBubble";
import { PUBLIC_BASE, fetchPublicWithRetry } from "../lib/api";
import { getToken } from "../lib/auth";

const BACKEND_URL = PUBLIC_BASE;

const HOT_SOURCE_META = {
  open_house_rsvp: { label: "RSVP", color: "bg-amber-500/15 text-amber-300 border-amber-400/30", icon: "CalendarCheck" },
  question: { label: "Question", color: "bg-blue-500/15 text-blue-300 border-blue-400/30", icon: "MessageCircle" },
  email_gate: { label: "Email", color: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30", icon: "Mail" },
  anon: { label: "Repeat viewer", color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30", icon: "Eye" },
};

const STATUS_META = {
  active: { label: "Active", color: "text-emerald-400 bg-emerald-500/10" },
  pending: { label: "Pending", color: "text-amber-400 bg-amber-500/10" },
  sold: { label: "Sold", color: "text-zinc-300 bg-zinc-500/20" },
  draft: { label: "Draft", color: "text-blue-400 bg-blue-500/10" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDate(iso);
}

export default function RealtorDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null); // null | "transient" | "not_found"
  const [retrying, setRetrying] = useState(false);

  const loadFirst = (opts = {}) => {
    setErr(null);
    setRetrying(opts.fromUserClick === true);
    return fetchPublicWithRetry(`${BACKEND_URL}/api/public/realtor/${token}`)
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        setErr(e?.kind === "not_found" ? "not_found" : "transient");
      })
      .finally(() => setRetrying(false));
  };

  useEffect(() => {
    let cancelled = false;
    let firstLoad = true;
    const load = () => {
      if (firstLoad) {
        firstLoad = false;
        if (!cancelled) loadFirst();
      } else {
        // Polling refreshes — silently keep prior data on transient errors
        // so the dashboard never flickers while open.
        axios
          .get(`${BACKEND_URL}/api/public/realtor/${token}`)
          .then((r) => !cancelled && setData(r.data))
          .catch(() => {});
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (err) {
    return (
      <PublicLinkError
        kind={err}
        retrying={retrying}
        onRetry={() => loadFirst({ fromUserClick: true })}
        testId="dashboard-invalid"
        context="your dashboard"
      />
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Icons.Loader2 size={20} className="text-zinc-600 animate-spin" />
      </div>
    );
  }

  const { realtor, company, totals, listings } = data;
  const brand = company?.color || null;
  const accent = brand || "#FFFFFF";

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      style={{ "--brand": accent }}
      data-testid="realtor-dashboard"
    >
      {brand && <div className="h-1 w-full" style={{ background: brand }} />}

      {/* Header — credibility hero + live + engagement */}
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-6 space-y-5">
        <RealtorCredibilityCard realtor={realtor} brand={brand} />

        <div className="flex flex-wrap items-center gap-3">
          <LiveViewerBadge kind="dashboard" token={token} brand={brand} />
          <div className="ml-auto flex items-center gap-2">
            <EmbedSnippet
              kind="dashboard"
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={`${realtor.name || "Realtor"} — Listing portfolio`}
              brand={brand}
            />
            <ShareRow
              title={`${realtor.name || "Realtor"} · listing portfolio`}
              subtitle={realtor.brokerage_name || company?.name}
              brand={brand}
            />
          </div>
        </div>

        <PublicEngagementStrip token={token} brand={brand} />

        <BookingsPanel token={token} brand={brand} />

        <PublicInquiriesPanel token={token} brand={brand} />
      </div>

      {/* Stats strip */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-3 gap-3 mb-8">
          <Stat
            label="Listings"
            value={totals.listings}
            icon={<Icons.Home size={14} />}
            brand={brand}
            testid="stat-listings"
          />
          <Stat
            label="Reels"
            value={totals.reels}
            icon={<Icons.Film size={14} />}
            brand={brand}
            testid="stat-reels"
          />
          <Stat
            label="Total Views"
            value={totals.views}
            icon={<Icons.Eye size={14} />}
            brand={brand}
            highlight
            testid="stat-views"
          />
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        {listings.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl">
            <Icons.Inbox size={24} className="mx-auto text-zinc-700 mb-2" />
            <div className="text-zinc-500 text-sm">
              No shared reels yet. Your listing videos will appear here once
              they're published.
            </div>
          </div>
        ) : (
          <>
            <HotLeadsPanel token={token} brand={brand} />
            <HotCitiesPanel listings={listings} brand={brand} />
            <WeeklyDigestPanel token={token} brand={brand} />
            <div className="space-y-6">
              {listings.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  brand={brand}
                  onChanged={() => {
                    // Trigger a refresh of the parent data by nudging state.
                    // The parent polls every 30s; this is instant.
                    axios
                      .get(`${BACKEND_URL}/api/public/realtor/${token}`)
                      .then((r) => setData(r.data))
                      .catch(() => {});
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 border-t border-white/5 text-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-700">
          Live · Refreshes every 30s
        </div>
      </div>

      {/* Realtor's AI chatbot — floating bubble.
          Renders nothing when the realtor hasn't configured a URL.
          On the dashboard the viewer can be looking at ANY of the
          realtor's listings, so we pass the full address list so the
          bot can ask "which one are you interested in?". */}
      <RealtorChatbotBubble
        url={realtor?.chatbot_embed_url}
        realtorName={realtor?.name}
        brandColor={brand}
        context={{
          page: "dashboard",
          realtor_name: realtor?.name,
          realtor_phone: realtor?.phone,
          realtor_email: realtor?.email,
          realtor_dashboard_token: token,
          listing_count: listings.length,
          listing_addresses: listings.map((l) => l.address).filter(Boolean),
        }}
      />
    </div>
  );
}

function Stat({ label, value, icon, brand, highlight, testid }) {
  return (
    <div
      className="bg-[#0A0A0A] border rounded-2xl px-4 py-4"
      style={{
        borderColor:
          highlight && brand ? `${brand}60` : "rgb(255 255 255 / 0.08)",
      }}
      data-testid={testid}
    >
      <div
        className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase"
        style={{ color: highlight && brand ? brand : "#71717a" }}
      >
        {icon}
        {label}
      </div>
      <div
        className="font-display text-3xl sm:text-4xl mt-1"
        style={{ color: highlight && brand ? brand : "#ffffff" }}
      >
        {value}
      </div>
    </div>
  );
}

function ListingCard({ listing, brand, onChanged }) {
  const status = listing.status || "active";
  const meta = STATUS_META[status] || STATUS_META.active;
  const [editOpen, setEditOpen] = useState(false);
  const [sellerPortalToken, setSellerPortalToken] = useState(
    listing.seller_portal_token || null
  );

  const downloadOhFlyer = async () => {
    try {
      const authTok = getToken();
      if (!authTok) {
        window.alert("Please sign in (in another tab) to download the flyer.");
        return;
      }
      const shareBase =
        typeof window !== "undefined" ? window.location.origin : "";
      const r = await axios.get(
        `${BACKEND_URL}/api/listings/${listing.id}/open-house-flyer.pdf?share_base=${encodeURIComponent(shareBase)}`,
        {
          headers: { Authorization: `Bearer ${authTok}` },
          responseType: "blob",
        }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const slug = (listing.address || "open-house").replace(/\s+/g, "_").slice(0, 60);
      a.href = url;
      a.download = `${slug}_open_house_flyer.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        window.alert("This dashboard is a public link — to download the flyer, sign in to the app in another tab first.");
      } else if (code === 400) {
        window.alert("No open-house date set yet. Add one first (Edit).");
      } else {
        window.alert("Flyer download failed.");
      }
    }
  };

  const downloadFlyer = async () => {
    try {
      const authTok = getToken();
      if (!authTok) {
        window.alert("Please sign in (in another tab) to download the flyer.");
        return;
      }
      const shareBase =
        typeof window !== "undefined" ? window.location.origin : "";
      // Prefer the first shareable reel so the QR scans straight to the video.
      const reel = (listing.reels || []).find((x) => x.share_token);
      const params = new URLSearchParams({
        share_base: shareBase,
        include_open_house: "false",
      });
      if (reel) params.set("reel_id", reel.id);
      const r = await axios.get(
        `${BACKEND_URL}/api/listings/${listing.id}/flyer.pdf?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${authTok}` },
          responseType: "blob",
        }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const slug = (listing.address || "listing").replace(/\s+/g, "_").slice(0, 60);
      a.href = url;
      a.download = `${slug}_flyer.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        window.alert("This dashboard is a public link — to download the flyer, sign in to the app in another tab first.");
      } else {
        window.alert("Flyer download failed.");
      }
    }
  };

  const copySellerPortal = async () => {
    try {
      const authTok = getToken();
      let tok = sellerPortalToken;
      if (!tok) {
        if (!authTok) {
          window.alert("Sign in to the app in another tab to mint a seller-portal link.");
          return;
        }
        const { data } = await axios.post(
          `${BACKEND_URL}/api/listings/${listing.id}/seller-portal`,
          {},
          { headers: { Authorization: `Bearer ${authTok}` } }
        );
        tok = data.seller_portal_token;
        setSellerPortalToken(tok);
      }
      const url = `${window.location.origin}/seller/${tok}`;
      await navigator.clipboard.writeText(url);
      window.alert(`Seller Portal link copied — send to your seller:\n\n${url}`);
    } catch {
      window.alert("Could not mint seller portal.");
    }
  };

  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden"
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="flex flex-col sm:flex-row">
        {listing.cover_image_url ? (
          <img
            src={`${BACKEND_URL}${listing.cover_image_url}`}
            alt=""
            className="sm:w-48 w-full h-40 sm:h-auto object-cover shrink-0"
          />
        ) : (
          <div className="sm:w-48 w-full h-40 sm:h-auto bg-white/5 flex items-center justify-center shrink-0">
            <Icons.Home size={24} className="text-zinc-700" />
          </div>
        )}
        <div className="flex-1 p-5 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <div className="font-display text-lg text-white flex-1 min-w-0 truncate">
              {listing.address || "Untitled listing"}
            </div>
            <span
              className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${meta.color}`}
            >
              {meta.label}
            </span>
          </div>
          {listing.listing_url && (
            <a
              href={listing.listing_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white"
            >
              <Icons.ExternalLink size={11} />
              View full listing
            </a>
          )}

          <ListingStatsCard
            stats={listing.stats}
            brand={brand}
            variant="compact"
            data-testid={`dashboard-stats-${listing.id}`}
          />

          {/* Action row: Edit · OH flyer · Seller Portal */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setEditOpen(true)}
              className="text-[11px] uppercase tracking-wider bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              data-testid={`dashboard-edit-${listing.id}`}
            >
              <Icons.Edit3 size={11} />
              Edit listing
            </button>
            <button
              onClick={downloadFlyer}
              className="text-[11px] uppercase tracking-wider bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title="Download A4 flyer with QR to the reel — no open-house banner"
              data-testid={`dashboard-flyer-${listing.id}`}
            >
              <Icons.FileDown size={11} />
              Flyer
            </button>
            <button
              onClick={downloadOhFlyer}
              className="text-[11px] uppercase tracking-wider bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/30 text-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title="Download A4 flyer with big OPEN HOUSE banner + QR that tracks event scans"
              data-testid={`dashboard-oh-flyer-${listing.id}`}
            >
              <Icons.Printer size={11} />
              Open house flyer
            </button>
            <button
              onClick={copySellerPortal}
              className="text-[11px] uppercase tracking-wider bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title="Copy a read-only branded dashboard link to send to the seller"
              data-testid={`dashboard-seller-portal-${listing.id}`}
            >
              <Icons.Share2 size={11} />
              Seller portal link
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {listing.reels.map((r, idx) => (
              <ReelRow
                key={r.id}
                reel={r}
                brand={brand}
                listingAddress={listing.address}
                index={idx}
              />
            ))}
          </div>
        </div>
      </div>

      {editOpen && (
        <EditListingDrawer
          listing={listing}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

function _normalizeForCompare(s) {
  if (!s) return "";
  return s.toLowerCase().replace(/[\s,.\-_]+/g, "");
}

function ReelRow({ reel, brand, listingAddress, index }) {
  const shareUrl = `/share/${reel.share_token}`;
  const done = reel.status === "done";
  const pins = reel.pins || [];
  const retention = reel.retention || [];
  const retentionMax = Math.max(1, ...retention);
  const totalStarts = retention[0] || 0;
  // If the reel title is just (a slight variant of) the listing address, the
  // listing card heading already shows it — don't repeat it on every reel row.
  const titleMatchesAddress =
    _normalizeForCompare(reel.title) === _normalizeForCompare(listingAddress);
  const displayTitle = titleMatchesAddress
    ? `Listing reel${typeof index === "number" ? ` · #${index + 1}` : ""}`
    : reel.title || "Listing Reel";
  return (
    <div
      className="bg-black/40 border border-white/5 rounded-xl px-3 py-2.5"
      data-testid={`reel-row-${reel.id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: brand ? `${brand}1A` : "rgb(255 255 255 / 0.05)",
            color: brand || "#a1a1aa",
          }}
        >
          <Icons.Film size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">
            {displayTitle}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>Created {fmtDate(reel.created_at)}</span>
            {reel.last_viewed && (
              <>
                <span className="text-zinc-700">·</span>
                <span>Last viewed {fmtRelative(reel.last_viewed)}</span>
              </>
            )}
            {!done && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-amber-400">Rendering…</span>
              </>
            )}
          </div>
        </div>
        <div
          className="flex items-center gap-1 text-sm font-mono tabular-nums px-2.5 py-1 rounded-md"
          style={{
            background: brand ? `${brand}1A` : "rgb(255 255 255 / 0.05)",
            color: brand || "#ffffff",
          }}
          title="Total views"
          data-testid={`reel-views-${reel.id}`}
        >
          <Icons.Eye size={12} />
          {reel.view_count}
        </div>
        <ReactionsBadgeRow reactions={reel.reactions} reelId={reel.id} />
        {done && (
          <Link
            to={shareUrl}
            target="_blank"
            className="inline-flex items-center gap-1 text-xs text-zinc-300 border border-white/15 rounded-lg px-3 py-1.5 hover:bg-white/5"
            data-testid={`reel-open-${reel.id}`}
          >
            <Icons.Play size={12} />
            Watch
          </Link>
        )}
      </div>

      {/* Analytics — geo pins + retention */}
      {(pins.length > 0 || totalStarts > 0) && (
        <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Geo */}
          <div data-testid={`reel-geo-${reel.id}`}>
            <div
              className="flex items-center gap-1.5 text-[9px] tracking-[0.25em] uppercase mb-2"
              style={{ color: brand || "#71717a" }}
            >
              <Icons.MapPin size={10} />
              Viewed from
            </div>
            {pins.length === 0 ? (
              <div className="text-[11px] text-zinc-600 italic">
                Geo data will appear after the next view.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {pins.slice(0, 8).map((p, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-[11px] text-zinc-300"
                    title={`${p.city}${p.country ? ", " + p.country : ""}`}
                  >
                    <span className="truncate max-w-[110px]">{p.city}</span>
                    <span
                      className="font-mono tabular-nums text-[10px]"
                      style={{ color: brand || "#a1a1aa" }}
                    >
                      {p.count}
                    </span>
                  </div>
                ))}
                {pins.length > 8 && (
                  <div className="text-[10px] text-zinc-600 self-center">
                    + {pins.length - 8} more
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Retention */}
          <div data-testid={`reel-retention-${reel.id}`}>
            <div
              className="flex items-center gap-1.5 text-[9px] tracking-[0.25em] uppercase mb-2"
              style={{ color: brand || "#71717a" }}
            >
              <Icons.BarChart3 size={10} />
              Watch retention
            </div>
            {totalStarts === 0 ? (
              <div className="text-[11px] text-zinc-600 italic">
                Retention data will appear once viewers start watching.
              </div>
            ) : (
              <div>
                <div className="flex items-end gap-[2px] h-10">
                  {retention.map((v, i) => {
                    const h = Math.max(2, (v / retentionMax) * 100);
                    return (
                      <div
                        key={i}
                        className="flex-1 rounded-t-sm transition-all"
                        style={{
                          height: `${h}%`,
                          background: brand || "#ffffff",
                          opacity: 0.25 + (v / retentionMax) * 0.75,
                        }}
                        title={`${i * 10}-${(i + 1) * 10}%: ${v} viewers`}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1 text-[9px] text-zinc-600 font-mono">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function HotLeadsPanel({ token, brand }) {
  const [leads, setLeads] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      axios
        .get(`${BACKEND_URL}/api/dashboard/${token}/hot-leads`)
        .then((r) => !cancelled && setLeads(r.data?.leads || []))
        .catch(() => !cancelled && setLeads([]));
    load();
    const t = setInterval(load, 45000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [token]);

  const count = leads?.length ?? 0;
  const namedCount = (leads || []).filter((l) => l.kind === "named").length;

  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6 overflow-hidden"
      data-testid="hot-leads-panel"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5"
      >
        <Icons.Flame
          size={16}
          className="shrink-0"
          style={{ color: brand || "#f59e0b" }}
        />
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: brand || "#f59e0b" }}
          >
            Hot Lead Inbox
          </div>
          <div className="text-sm text-white mt-0.5">
            {count === 0
              ? "No hot leads yet"
              : `${namedCount} named · ${count - namedCount} anonymous repeat viewer${
                  count - namedCount === 1 ? "" : "s"
                }`}
          </div>
        </div>
        <Icons.ChevronDown
          size={16}
          className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-white/5">
          {leads === null ? (
            <div className="p-6 text-center text-xs text-zinc-600">
              <Icons.Loader2 size={16} className="animate-spin inline-block" />
            </div>
          ) : leads.length === 0 ? (
            <div className="p-6 text-[12px] text-zinc-500 leading-relaxed">
              No leads yet. Add an email gate & open-house RSVP to your share
              page to start capturing named contacts — then come back here.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {leads.slice(0, 15).map((l, i) => (
                <LeadRow key={l.id || l.ip || i} lead={l} brand={brand} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LeadRow({ lead, brand }) {
  const srcKey = lead.kind === "anon" ? "anon" : lead.source;
  const meta = HOT_SOURCE_META[srcKey] || HOT_SOURCE_META.anon;
  const Icon = Icons[meta.icon] || Icons.Eye;
  const title =
    lead.name || lead.email || lead.phone || `Viewer · ${lead.ip || "?"}`;
  const sub = [
    lead.city,
    lead.country_code,
    lead.email && lead.name ? lead.email : null,
    lead.phone && (lead.name || lead.email) ? lead.phone : null,
    lead.views ? `${lead.views} views` : null,
    lead.reels_watched && lead.reels_watched > 1 ? `${lead.reels_watched} reels` : null,
    lead.party_size ? `party of ${lead.party_size}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      className="flex items-start gap-3 px-5 py-3"
      data-testid="hot-lead-row"
    >
      <div
        className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${meta.color}`}
      >
        <Icon size={13} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="text-sm text-white truncate">{title}</div>
          <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">
            {meta.label}
          </span>
        </div>
        {sub && <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{sub}</div>}
        {lead.message && (
          <div className="mt-1 text-[12px] text-zinc-300 bg-white/5 rounded-md px-2.5 py-1.5">
            "{lead.message}"
          </div>
        )}
      </div>
      {(lead.email || lead.phone) && (
        <div className="flex gap-1 shrink-0">
          {lead.email && (
            <a
              href={`mailto:${lead.email}`}
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white"
              title={`Email ${lead.email}`}
            >
              <Icons.Mail size={12} />
            </a>
          )}
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white"
              title={`Call ${lead.phone}`}
            >
              <Icons.Phone size={12} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function WeeklyDigestPanel({ token, brand }) {
  const [digest, setDigest] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/dashboard/${token}/weekly-digest`)
      .then((r) => setDigest(r.data))
      .catch(() => setDigest(null));
  }, [token]);

  if (!digest) return null;

  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6 overflow-hidden"
      data-testid="weekly-digest-panel"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5"
      >
        <Icons.Mail
          size={16}
          className="shrink-0"
          style={{ color: brand || "#60a5fa" }}
        />
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: brand || "#60a5fa" }}
          >
            Weekly digest preview · {digest.week_of}
          </div>
          <div className="text-sm text-white mt-0.5 truncate">
            {digest.insights?.[0] || "Weekly recap"}
          </div>
        </div>
        <Icons.ChevronDown
          size={16}
          className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-white/5 p-5 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <DigestStat label="This week" value={digest.summary.week_views} sub="views" />
            <DigestStat label="All time" value={digest.summary.total_views_all_time} sub="views" />
            <DigestStat label="New leads" value={digest.summary.leads_this_week} />
            <DigestStat label="OH scans" value={digest.summary.oh_scans_all_time} />
          </div>
          {(digest.insights || []).length > 0 && (
            <ul className="text-[13px] text-zinc-300 space-y-1 pt-1">
              {digest.insights.map((ins, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-zinc-600">—</span>
                  {ins}
                </li>
              ))}
            </ul>
          )}
          {(digest.top_cities || []).length > 0 && (
            <div className="pt-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-1.5">
                Top cities (7d)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {digest.top_cities.map((c, i) => (
                  <span
                    key={i}
                    className="text-[11px] bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-zinc-300"
                  >
                    {c.city} · {c.views}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] text-zinc-600 pt-1 border-t border-white/5">
            Automatic Monday-9am email delivery is coming in a later update —
            until then, copy-paste any of the lines above to your weekly
            seller email.
          </div>
        </div>
      )}
    </div>
  );
}

function DigestStat({ label, value, sub }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
      <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500">
        {label}
      </div>
      <div className="font-display text-2xl text-white mt-0.5">{value ?? 0}</div>
      {sub && <div className="text-[10px] text-zinc-500">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Hot Cities — aggregated geo across every reel of every listing the
// realtor owns.  Renders a horizontal-bar "poor man's heatmap" that
// answers the one question sellers always ask: "Who's even watching
// these videos?"  We reuse the pins already embedded in each reel
// payload so no extra API call is needed.
// ─────────────────────────────────────────────────────────────────────
function HotCitiesPanel({ listings, brand }) {
  const [open, setOpen] = useState(false);

  const rows = (() => {
    const agg = new Map();
    (listings || []).forEach((l) => {
      (l.reels || []).forEach((r) => {
        (r.pins || []).forEach((p) => {
          const key = `${p.city || "Unknown"}|${p.country_code || ""}`;
          const cur = agg.get(key) || {
            city: p.city || "Unknown",
            country_code: p.country_code,
            count: 0,
            listing_count: new Set(),
          };
          cur.count += p.count || 0;
          cur.listing_count.add(l.id);
          agg.set(key, cur);
        });
      });
    });
    return Array.from(agg.values())
      .map((r) => ({ ...r, listing_count: r.listing_count.size }))
      .sort((a, b) => b.count - a.count);
  })();

  if (rows.length === 0) return null;

  const total = rows.reduce((s, r) => s + r.count, 0);
  const top = rows.slice(0, 8);
  const rest = rows.length > 8 ? rows.length - 8 : 0;
  const max = top[0].count || 1;
  const accent = brand || "#60a5fa";

  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6 overflow-hidden"
      data-testid="hot-cities-panel"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5"
        data-testid="hot-cities-toggle"
      >
        <Icons.Globe2 size={16} className="shrink-0" style={{ color: accent }} />
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: accent }}
          >
            Hot cities · where your buyers watch from
          </div>
          <div className="text-sm text-white mt-0.5 truncate">
            {rows.length === 1
              ? `All ${total} views from ${top[0].city}`
              : `${total} views across ${rows.length} cit${rows.length === 1 ? "y" : "ies"} — top: ${top[0].city} (${top[0].count})`}
          </div>
        </div>
        <Icons.ChevronDown
          size={16}
          className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-white/5 px-5 py-4 space-y-2">
          {top.map((r, i) => {
            const pct = Math.max(6, Math.round((r.count / max) * 100));
            const share = total > 0 ? Math.round((r.count / total) * 100) : 0;
            return (
              <div
                key={`${r.city}-${r.country_code}-${i}`}
                className="flex items-center gap-3"
                data-testid={`hot-city-row-${i}`}
              >
                <div className="w-32 sm:w-40 shrink-0 text-sm text-white truncate flex items-center gap-2">
                  <Icons.MapPin size={11} style={{ color: accent }} />
                  <span className="truncate">{r.city}</span>
                  {r.country_code && (
                    <span className="text-[10px] text-zinc-500">
                      {r.country_code}
                    </span>
                  )}
                </div>
                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: accent, opacity: 0.85 }}
                  />
                </div>
                <div className="w-20 text-right shrink-0">
                  <span className="font-mono text-sm" style={{ color: accent }}>
                    {r.count}
                  </span>
                  <span className="text-[10px] text-zinc-500 ml-1">
                    · {share}%
                  </span>
                </div>
              </div>
            );
          })}
          {rest > 0 && (
            <div className="text-[11px] text-zinc-500 text-center pt-2 border-t border-white/5">
              + {rest} more cit{rest === 1 ? "y" : "ies"}
            </div>
          )}
          <div className="text-[10px] text-zinc-600 pt-1">
            Tip: if a city dominates, share the reel to LinkedIn groups or
            Facebook neighbourhood pages in that area for outsized reach.
          </div>
        </div>
      )}
    </div>
  );
}

function EditListingDrawer({ listing, onClose, onSaved }) {
  // Inline editor so a realtor can quickly change the status, price, and
  // open-house date/time directly from the dashboard — the #1 friction point
  // (walking to another tab to tweak OH info before printing a flyer).
  const [status, setStatus] = useState(listing.status || "active");
  const [price, setPrice] = useState(listing.stats?.price ?? "");
  const [priceLabel, setPriceLabel] = useState(listing.stats?.price_label || "");
  const [ohDate, setOhDate] = useState(listing.open_house?.date || "");
  const [ohTime, setOhTime] = useState(listing.open_house?.time || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const authTok = getToken();
      if (!authTok) {
        setErr("Sign in to the main app in another tab to save listing edits.");
        setSaving(false);
        return;
      }
      const patch = {
        status,
        stats: {
          ...(listing.stats || {}),
          price: price === "" ? null : Number(price),
          price_label: priceLabel || null,
        },
        open_house: ohDate || ohTime
          ? { date: ohDate || null, time: ohTime || null }
          : null,
      };
      await axios.patch(
        `${BACKEND_URL}/api/listings/${listing.id}`,
        patch,
        { headers: { Authorization: `Bearer ${authTok}` } }
      );
      onSaved?.();
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          "Could not save — open the main app in another tab so you're authenticated, then try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-listing-drawer"
    >
      <div
        className="w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icons.Edit3 size={14} className="text-white" />
            <div className="font-display text-lg text-white">Quick edit</div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
            data-testid="edit-listing-close"
          >
            <Icons.X size={16} />
          </button>
        </div>
        <div className="text-[11px] text-zinc-500 mb-4 truncate">
          {listing.address}
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">
              Status
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {["active", "pending", "sold", "draft"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`text-xs rounded-lg py-1.5 border transition-all ${
                    status === s
                      ? "bg-white text-black border-white"
                      : "bg-black/40 border-white/10 text-zinc-300 hover:border-white/30"
                  }`}
                  data-testid={`edit-status-${s}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">
                Price
              </div>
              <input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                type="number"
                placeholder="—"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm focus:outline-none focus:border-white/30"
                data-testid="edit-price"
              />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">
                Price label
              </div>
              <input
                value={priceLabel}
                onChange={(e) => setPriceLabel(e.target.value)}
                placeholder="Guide price"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                data-testid="edit-price-label"
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">
              Open house
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={ohDate}
                onChange={(e) => setOhDate(e.target.value)}
                placeholder="Sat 18 Oct 2025"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                data-testid="edit-oh-date"
              />
              <input
                value={ohTime}
                onChange={(e) => setOhTime(e.target.value)}
                placeholder="2 - 4 PM"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                data-testid="edit-oh-time"
              />
            </div>
          </div>
          {err && (
            <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-400/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-white text-black font-medium rounded-lg py-2.5 hover:bg-zinc-200 disabled:opacity-30 flex items-center justify-center gap-2 mt-2"
            data-testid="edit-listing-save"
          >
            {saving ? (
              <Icons.Loader2 size={14} className="animate-spin" />
            ) : (
              <Icons.Check size={14} />
            )}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


// Compact reaction-count strip rendered inside each reel row on the
// dashboard.  Mirrors the emoji set on the public share page so the
// realtor sees exactly what viewers tap.  Hides itself entirely if the
// reel has no reactions yet (avoids visual noise on brand-new reels).
const REACTION_KINDS = [
  { id: "love", emoji: "❤️" },
  { id: "fire", emoji: "🔥" },
  { id: "wow", emoji: "😍" },
  { id: "thumbs", emoji: "👍" },
];

function ReactionsBadgeRow({ reactions, reelId }) {
  const counts = reactions || {};
  const total = REACTION_KINDS.reduce(
    (n, k) => n + Number(counts[k.id] || 0),
    0
  );
  if (total <= 0) return null;
  return (
    <div
      className="hidden sm:flex items-center gap-1 text-[11px] font-mono tabular-nums"
      title="Viewer reactions"
      data-testid={`reel-reactions-${reelId}`}
    >
      {REACTION_KINDS.map((k) => {
        const c = Number(counts[k.id] || 0);
        if (c <= 0) return null;
        return (
          <span
            key={k.id}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-white/10"
            data-testid={`reel-reaction-count-${reelId}-${k.id}`}
          >
            <span className="text-[12px] leading-none">{k.emoji}</span>
            <span className="text-zinc-300">{c}</span>
          </span>
        );
      })}
    </div>
  );
}

